import { useEffect } from "react";

// Production canonical origin (live site). Used for canonical + OG/Twitter URLs.
export const SITE_URL = "https://hook2income.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo-full.png`;

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, created };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, created };
}

// Lightweight, dependency-free per-page SEO. Sets <title>, meta description,
// canonical, Open Graph + Twitter card tags and optional JSON-LD structured data.
export default function useSeo({
  title,
  description,
  path = "",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const prevTitle = document.title;
    const url = `${SITE_URL}${path}`;
    if (title) document.title = title;

    const created = [];
    const track = (r) => { if (r.created) created.push(r.el); };

    const metas = [
      ["name", "description", description],
      ["name", "robots", noindex ? "noindex, nofollow" : "index, follow"],
      ["property", "og:title", title],
      ["property", "og:description", description],
      ["property", "og:type", type],
      ["property", "og:url", url],
      ["property", "og:image", image],
      ["property", "og:site_name", "Hook2Income AI"],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", title],
      ["name", "twitter:description", description],
      ["name", "twitter:image", image],
    ];
    metas.forEach(([attr, key, content]) => {
      if (content) track(upsertMeta(attr, key, content));
    });
    track(upsertLink("canonical", url));

    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, path, image, type, noindex, jsonLdKey]);
}
